import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/** `{ selector, element }` — which selector the element was found for. */
const NOT_FOUND = { selector: null, element: null };
/** `{ of, rect }` — which `found` entry the rect was measured from. */
const NOT_MEASURED = { of: null, rect: null };

const sameBox = (a, b) =>
    Boolean(a && b) && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

/** Same box and, for anchors made of several boxes (`parts`), the same boxes in the same order. */
const sameRect = (a, b) =>
    sameBox(a, b) &&
    (a.parts?.length ?? 0) === (b.parts?.length ?? 0) &&
    (a.parts ?? []).every((part, index) => sameBox(part, b.parts[index]));

const toRect = (domRect) => ({
    top: domRect.top,
    left: domRect.left,
    width: domRect.width,
    height: domRect.height,
});

const isInViewport = (rect) =>
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.top + rect.height <= window.innerHeight &&
    rect.left + rect.width <= window.innerWidth;

const queryAll = (selector) => {
    try {
        return Array.from(document.querySelectorAll(selector));
    } catch {
        return [];
    }
};

const sameList = (a, b) => a.length === b.length && a.every((item, index) => item === b[index]);

/**
 * Follow document-level elements that are not inside the anchor but shape
 * its reported rect (a dropdown panel portaled to `document.body`, see
 * `anchorRects.unionRects`). Calls `onChange` when one of them mounts or
 * unmounts, and while mounted when it resizes or its attributes (inline
 * position) or content change. Returns a disposer.
 */
const observeDocumentElements = (selectors, onChange) => {
    if (typeof MutationObserver !== 'function') return () => {};
    let tracked = [];
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(onChange) : null;
    const elementObserver = new MutationObserver(onChange);
    const sync = () => {
        const found = selectors.flatMap(queryAll);
        if (sameList(found, tracked)) return;
        resizeObserver?.disconnect();
        elementObserver.disconnect();
        found.forEach((node) => {
            resizeObserver?.observe(node);
            elementObserver.observe(node, { attributes: true, childList: true, subtree: true });
        });
        tracked = found;
        onChange();
    };
    const bodyObserver = new MutationObserver(sync);
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    sync();
    return () => {
        bodyObserver.disconnect();
        resizeObserver?.disconnect();
        elementObserver.disconnect();
    };
};

/**
 * Resolve a coach-mark anchor from a CSS selector and follow it around.
 *
 * - Waits for the element to appear (MutationObserver) and notices when it is
 *   removed or replaced, so steps whose anchor renders late still attach.
 * - Scrolls it into view once and tracks its viewport rect through
 *   ResizeObserver, a document-wide MutationObserver (siblings mounting or
 *   swapping shift the anchor without resizing it) plus capture-phase
 *   scroll/resize/input listeners (rAF-coalesced). `anchorRect(element,
 *   rect)` can narrow the reported rect, e.g. to the text an input shows
 *   (`utils/anchorRects.js`).
 * - `observe`: selectors of document-level elements (portaled panels) whose
 *   mount, move or unmount must also trigger a re-measure.
 * - `scrollIntoView: false` skips the one-off scroll (a hinted option inside
 *   a menu the anchor already brought on screen).
 * - Optionally treats DOM events on the element as "action done"
 *   (`completeOnEvents` + `onComplete`), for click-in-place steps.
 *
 * Returns `{ element, rect }`; both are `null` until the anchor exists, and
 * `rect` is only ever the box measured from the *current* `selector`'s
 * element. When the selector changes (the runner moves to the next step) the
 * previous anchor's element and rect are dropped at once instead of being
 * reported for a render or two, and the new anchor is found and measured in
 * layout effects — before the browser paints — so a step never shows up
 * around the control the previous step pointed at.
 */
export function useWalkthroughTarget(
    selector,
    { enabled = true, anchorRect, observe, scrollIntoView = true, completeOnEvents, onComplete } = {}
) {
    // Each resolved value is stored together with the input it was resolved
    // from and only reported while that input is still the current one.
    const [found, setFound] = useState(NOT_FOUND);
    const element = enabled && found.selector === selector ? found.element : null;
    const [measured, setMeasured] = useState(NOT_MEASURED);
    const rect = element && measured.of === found ? measured.rect : null;
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;
    const anchorRectRef = useRef(anchorRect);
    anchorRectRef.current = anchorRect;

    // Find (and keep finding) the anchor element.
    useLayoutEffect(() => {
        if (!enabled || !selector || typeof document === 'undefined') {
            setFound((prev) => (prev === NOT_FOUND ? prev : NOT_FOUND));
            return undefined;
        }
        let current = null;
        let frame = 0;
        const check = () => {
            frame = 0;
            let next = null;
            try {
                next = document.querySelector(selector);
            } catch {
                next = null;
            }
            if (next !== current) {
                current = next;
                setFound({ selector, element: next });
            }
        };
        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(check);
        };
        check();
        const observer = typeof MutationObserver === 'function' ? new MutationObserver(schedule) : null;
        observer?.observe(document.body, { childList: true, subtree: true, attributes: true });
        return () => {
            observer?.disconnect();
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [selector, enabled]);

    // Track the anchor's viewport rect.
    const observeKey = Array.isArray(observe) ? observe.join(',') : '';
    useLayoutEffect(() => {
        if (!element) return undefined;
        let frame = 0;
        const measure = () => {
            frame = 0;
            if (!element.isConnected) return;
            const box = toRect(element.getBoundingClientRect());
            const next = anchorRectRef.current ? anchorRectRef.current(element, box) : box;
            setMeasured((prev) => (prev.of === found && sameRect(prev.rect, next) ? prev : { of: found, rect: next }));
        };
        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(measure);
        };

        const initial = toRect(element.getBoundingClientRect());
        if (scrollIntoView && !isInViewport(initial) && typeof element.scrollIntoView === 'function') {
            element.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        }
        measure();

        const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
        resizeObserver?.observe(element);
        resizeObserver?.observe(document.documentElement);
        // DOM changes inside the anchor move a child-shaped `anchorRect`
        // without resizing the anchor (cards reordering); re-measure now and
        // again once any slide animation has settled.
        const onMutation = () => {
            schedule();
            const animations = element.getAnimations?.({ subtree: true }) || [];
            if (animations.length) Promise.all(animations.map((a) => a.finished)).then(schedule, () => {});
        };
        const mutationObserver = typeof MutationObserver === 'function' ? new MutationObserver(onMutation) : null;
        mutationObserver?.observe(element, { childList: true, subtree: true, attributes: true, characterData: true });
        // DOM changes *around* the anchor move it without resizing it or the
        // page (a sibling button mounting or swapping, e.g. the task header's
        // Train → Deploy once versions load): re-measure, rAF-coalesced, on
        // any document mutation. `measure` only re-renders when the box
        // actually changed.
        const layoutObserver = typeof MutationObserver === 'function' ? new MutationObserver(schedule) : null;
        layoutObserver?.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });
        window.addEventListener('scroll', schedule, true);
        window.addEventListener('resize', schedule);
        // Typing changes what a text-shaped `anchorRect` measures, not the box.
        element.addEventListener('input', schedule);
        const stopObserving = observeKey ? observeDocumentElements(observeKey.split(','), schedule) : null;
        return () => {
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            layoutObserver?.disconnect();
            stopObserving?.();
            window.removeEventListener('scroll', schedule, true);
            window.removeEventListener('resize', schedule);
            element.removeEventListener('input', schedule);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [element, found, observeKey, scrollIntoView]);

    // Completion by DOM event.
    const eventsKey = Array.isArray(completeOnEvents) ? completeOnEvents.join(',') : '';
    useEffect(() => {
        if (!element || !eventsKey) return undefined;
        const events = eventsKey.split(',');
        const handler = () => onCompleteRef.current?.();
        events.forEach((name) => element.addEventListener(name, handler));
        return () => events.forEach((name) => element.removeEventListener(name, handler));
    }, [element, eventsKey]);

    return { element, rect };
}

export default useWalkthroughTarget;
